import { useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { Download } from "lucide-react"
import { PageHeader } from "@/components/portal/PageHeader"
import { Loading, ErrorBox } from "@/components/portal/LoadingError"

/**
 * „Werbe-Absender" — von wem ein Betrieb Werbung, Newsletter und Spam bekommt (#172).
 *
 * Marlene am 13.10.2026: „eine Liste, damit ich das mit dem Kunden besprechen
 * kann, was wir abbestellen können".
 *
 * 🔴 Nur eine Liste. Die Seite bestellt nichts ab und ändert keine Mail.
 */

type Zeile = {
  absender: string; name: string; domain: string; anzahl: number
  als_werbung: number; im_spam_ordner: number; abmelde_link: boolean
  zuletzt: string | null; art: string
}
type Antwort = { tenant: string; tage: number; ab: string; mails: number; absender: Zeile[]; abgeschnitten: boolean }

const datum = (s: string | null) => (s ? s.slice(0, 10).split("-").reverse().join(".") : "—")

/** CSV mit Semikolon und BOM — so öffnet Excel sie ohne Import-Dialog. */
export function alsCsv(zeilen: Zeile[]): string {
  const feld = (v: string | number) => `"${String(v).replace(/"/g, '""')}"`
  const kopf = ["Absender", "Name", "Anzahl Mails", "Art", "Abmelde-Link", "Zuletzt"]
  const rows = zeilen.map((z) =>
    [z.absender, z.name, z.anzahl, z.art, z.abmelde_link ? "ja" : "nein", datum(z.zuletzt)].map(feld).join(";"))
  return "﻿" + [kopf.map(feld).join(";"), ...rows].join("\r\n")
}

export default function WerbeAbsenderPage() {
  const [tenant, setTenant] = useState("")
  const [tage, setTage] = useState(90)

  const tenantList = useQuery<{ slug: string; name?: string }[]>({
    queryKey: ["werbe-absender-tenants"],
    queryFn: async () => {
      const r = await fetch("/api/v1/tenants", { credentials: "include" })
      if (!r.ok) return []
      const d = await r.json()
      return Array.isArray(d.items) ? d.items : []
    },
    staleTime: 1000 * 60 * 5,
  })

  const { data, isLoading, error, refetch, isFetching } = useQuery<Antwort>({
    queryKey: ["werbe-absender", tenant, tage],
    enabled: !!tenant,
    queryFn: async () => {
      const p = new URLSearchParams({ tenant, tage: String(tage) })
      const r = await fetch(`/api/v1/werbe-absender?${p}`, { credentials: "include" })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      return r.json()
    },
  })

  const zeilen = data?.absender || []

  const herunterladen = () => {
    const blob = new Blob([alsCsv(zeilen)], { type: "text/csv;charset=utf-8" })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `werbe-absender-${tenant}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="px-10 py-8 max-w-[1100px]">
      <PageHeader title="Werbe-Absender" onRefresh={tenant ? () => refetch() : undefined} refreshing={isFetching} />

      <p className="text-[13px] text-muted-foreground mb-4 max-w-[72ch]">
        Von wem ein Betrieb Werbung, Newsletter und Spam bekommt, häufigste Absender zuerst. Gedacht als Liste
        für das Gespräch mit dem Kunden, was abbestellt werden kann. Die Seite zählt nur, sie bestellt nichts ab.
      </p>

      <div className="flex flex-wrap items-center gap-3 mb-4 text-[13px]">
        <label className="flex items-center gap-2">
          Betrieb
          <select className="pp-input" value={tenant} onChange={(e) => setTenant(e.target.value)} aria-label="Betrieb">
            <option value="">Bitte wählen</option>
            {(tenantList.data || []).map((t) => <option key={t.slug} value={t.slug}>{t.name || t.slug}</option>)}
          </select>
        </label>
        <label className="flex items-center gap-2">
          Zeitraum
          <select className="pp-input" value={tage} onChange={(e) => setTage(Number(e.target.value))} aria-label="Zeitraum">
            <option value={30}>30 Tage</option>
            <option value={90}>90 Tage</option>
          </select>
        </label>
        <button type="button" className="pp-btn pp-btn-sm inline-flex items-center gap-1.5"
          disabled={!zeilen.length} onClick={herunterladen}>
          <Download className="w-3.5 h-3.5" />Als CSV herunterladen
        </button>
      </div>

      {!tenant && <div className="pp-card p-4 text-[13px] text-muted-foreground">Wählen Sie oben einen Betrieb.</div>}
      {tenant && isLoading && <Loading />}
      {error && <ErrorBox error={error} />}

      {tenant && data && (
        <>
          <div className="text-[12.5px] text-muted-foreground mb-2">
            {zeilen.length} Absender, {data.mails} Mails seit {datum(data.ab)}.
            {" "}Ältere Mails haben wegen der Löschfrist keinen Absender mehr.
            {data.abgeschnitten && " Die Liste ist gekürzt, bitte einen kürzeren Zeitraum wählen."}
          </div>
          {zeilen.length === 0 ? (
            <div className="pp-card p-4 text-[13px]">In diesem Zeitraum wurde keine Werbung erkannt.</div>
          ) : (
            <div className="pp-card overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="text-left text-muted-foreground border-b">
                    <th className="px-3 py-2 font-medium">Absender</th>
                    <th className="px-3 py-2 font-medium text-right">Mails</th>
                    <th className="px-3 py-2 font-medium">Art</th>
                    <th className="px-3 py-2 font-medium">Abmelde-Link</th>
                    <th className="px-3 py-2 font-medium">Zuletzt</th>
                  </tr>
                </thead>
                <tbody>
                  {zeilen.map((z) => (
                    <tr key={z.absender} className="border-b last:border-0">
                      <td className="px-3 py-2">
                        <div>{z.name || z.domain}</div>
                        <div className="text-[12px] text-muted-foreground">{z.absender}</div>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">{z.anzahl}</td>
                      <td className="px-3 py-2">{z.art}</td>
                      <td className="px-3 py-2">{z.abmelde_link ? "ja" : "nein"}</td>
                      <td className="px-3 py-2 tabular-nums">{datum(z.zuletzt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="text-[12px] text-muted-foreground mt-3 max-w-[72ch]">
            „Verteiler mit Abmelde-Link“ sind Mails, die nicht als Werbung eingestuft wurden, aber einen
            Abmelde-Link tragen. Das können auch Rundschreiben von Lieferanten oder Verbänden sein.
          </p>
        </>
      )}
    </div>
  )
}
